import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, PencilSquareIcon, TrashIcon, PlusIcon, FunnelIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DataTable from '../ui/DataTable';
import Pagination from '../ui/Pagination';
import Select from 'react-select';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import priceSyncService from '../../api/priceSyncService';
import type { PriceSyncResponse } from '../../api/priceSyncService';
import MassSyncModal from '../modals/MassSyncModal';
import ExportProductStockButton from '../products/ExportProductStockButton';
import type { SyncResult } from '../../api/massSyncService';
import { Button, Switch } from '../ui';
import { FilterPanel, FilterSection } from '../ui/FilterPanel';
import { isProductInactiveInSearch } from '../../utils/productSearchUtils';
import { FLEXY_PRODUCTS_ALL_PATH } from '../../constants/productsApiPaths';
import {
  getBodegaFilterOptions,
  getDefaultSelectedBodegas,
  getFlexyStockFilterValue,
  getProductTableBodegaColumns,
  getStockDisplayValueForBodegas,
  getValidBodegaFilterKeys,
  isDefaultBodegasSelection,
  sanitizeSelectedBodegasForRole,
  type FlexyProductStockRecord,
  type ProductTableStockFilterKey
} from '../../utils/productTableBodegasConfig';


interface Product {
  product_id: number;
  sku: string;
  name: string;
  /** 0 = inactivo */
  estado?: number;
  description?: string;
  category_id: number;
  price: number;
  is_combo: boolean;
  tax_rate: number;
  stock: number;
  ean12?: string;
  peso?: number;
  longitud?: number;
  ancho?: number;
  alto?: number;
  instrucciones_empaque?: string;
  imagen?: string;
  components?: Array<{
    sku: string;
    quantity: number;
    name: string;
    price: number;
  }>;
}
/** Respuesta de GET /flexy/all */
interface FlexyAllResponse {
  products?: FlexyProductStockRecord[]
  productos?: FlexyProductStockRecord[]
}

function normalizeProductSku(raw: unknown): string {
  const sku = String(raw ?? '').trim()
  if (!sku) return ''
  return sku.replace(/^0+/, '') || sku
}

function buildFlexyProductMap(
  products: FlexyProductStockRecord[]
): Map<string, FlexyProductStockRecord> {
  const map = new Map<string, FlexyProductStockRecord>()
  for (const product of products) {
    const rawSku = String(product.sku ?? product.Sku ?? '').trim()
    if (!rawSku) continue
    map.set(rawSku, product)
    const normalized = normalizeProductSku(rawSku)
    if (normalized && normalized !== rawSku) {
      map.set(normalized, product)
    }
  }
  return map
}

function extractFlexyProductsFromResponse(
  data: FlexyAllResponse | undefined
): FlexyProductStockRecord[] {
  if (!data) return []
  if (Array.isArray(data.products)) return data.products
  if (Array.isArray(data.productos)) return data.productos
  return []
}

function renderFlexyStockFilterCell(
  flexyProduct: FlexyProductStockRecord | undefined,
  filterKey: ProductTableStockFilterKey,
  positiveClass: string
) {
  const qty = getFlexyStockFilterValue(flexyProduct, filterKey)
  if (qty === null) {
    return (
      <span
        className="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-500"
        title="Sin dato en esta bodega"
      >
        —
      </span>
    )
  }
  const badgeClass =
    qty > 0
      ? positiveClass
      : qty < 0
        ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-800'
  return (
    <span className={`px-2 py-1 rounded-full text-sm ${badgeClass}`}>
      {qty}
    </span>
  )
}

function getFlexyProductForSku(
  map: Map<string, FlexyProductStockRecord>,
  sku: string
): FlexyProductStockRecord | undefined {
  return map.get(sku) ?? map.get(normalizeProductSku(sku))
}

function resolveFlexyFallbackPrice(
  flexyProduct: FlexyProductStockRecord | undefined
): number {
  if (!flexyProduct) return 0
  const price = parseFloat(String(flexyProduct.price ?? flexyProduct.Precio ?? 0))
  return Number.isFinite(price) && price > 0 ? price : 0
}

/** Misma regla que en ProductForm (Venta General). */
const OFIMA_COD_VENTA_GENERAL_TABLE = '001'

const toBitProductFlag = (value: unknown, whenMissing: number): number => {
  if (value === true) return 1
  if (value === false) return 0
  if (value === undefined || value === null) return whenMissing
  const n = Number(value)
  return n === 1 ? 1 : 0
}

function mapApiProductForPut(d: Record<string, unknown>): Record<string, unknown> {
  return {
    ...d,
    product_id: Number(d.product_id),
    components: Array.isArray(d.components) ? d.components : [],
    estado: toBitProductFlag(d.estado, 1),
    crear_en_ofima: toBitProductFlag(d.crear_en_ofima, 1),
    idUnidad:
      d.idUnidad !== undefined && d.idUnidad !== null && String(d.idUnidad).trim() !== ''
        ? String(d.idUnidad)
        : 'UND',
    precios: Array.isArray(d.precios) ? d.precios : []
  }
}

function buildPutBodyFromProduct(product: Record<string, unknown>, productId: number): Record<string, unknown> {
  const crearOfima = Number(product.crear_en_ofima) === 1
  const body: Record<string, unknown> = {
    ...product,
    is_combo: Boolean(product.is_combo),
    estado: Number(product.estado),
    crear_en_ofima: Number(product.crear_en_ofima)
  }
  if (!crearOfima) {
    delete body.idUnidad
    delete body.precios
  } else {
    body.idUnidad = String(product.idUnidad ?? 'UND').trim() || 'UND'
    const preciosRaw = Array.isArray(product.precios) ? product.precios : []
    body.precios = preciosRaw.map((row: { codigoPrecio?: unknown; precio?: unknown }) => {
      const codigo = String(row.codigoPrecio).trim()
      const precio =
        codigo === OFIMA_COD_VENTA_GENERAL_TABLE
          ? Number(product.price) || 0
          : Number(row.precio)
      return { codigoPrecio: codigo, precio }
    })
  }
  body.product_id = Number(productId)
  return body
}

function getApiProductErrorMessageTable(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>
  const headline = typeof d.message === 'string' ? d.message.trim() : ''
  const errors = d.errors
  if (!Array.isArray(errors) || errors.length === 0) {
    return headline
  }
  const lines = errors.map((item) => {
    if (!item || typeof item !== 'object') return ''
    const row = item as Record<string, unknown>
    const sku = row.sku != null ? String(row.sku).trim() : ''
    const errRaw = row.error != null ? String(row.error) : ''
    if (sku && errRaw) return `${sku}: ${errRaw}`
    if (errRaw) return errRaw
    return sku || ''
  }).filter(Boolean)
  if (lines.length === 0) return headline
  const body = lines.join(' · ')
  if (headline && body) return `${headline} — ${body}`
  return body || headline
}

/** Caracteres visibles del nombre en la tabla; si hay más, se añade "..." (anticipado para no chocar con la columna siguiente). */
const PRODUCT_TABLE_NAME_MAX_CHARS = 22

function displayProductTableName(name: string | undefined | null): {
  text: string
  full: string
  truncated: boolean
} {
  const full = (name ?? '').trim()
  if (full.length <= PRODUCT_TABLE_NAME_MAX_CHARS) {
    return { text: full, full, truncated: false }
  }
  return {
    text: `${full.slice(0, PRODUCT_TABLE_NAME_MAX_CHARS)}...`,
    full,
    truncated: true
  }
}

/** Sync masiva de precios Ofima. Oculta en la lista hasta volver a habilitarla. */
const SHOW_MASS_PRICE_SYNC_BUTTON = false

const ProductsTable: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const roleId = user?.role_id;

  const bodegaColumns = useMemo(
    () => getProductTableBodegaColumns(roleId),
    [roleId]
  );
  const defaultSelectedBodegas = useMemo(
    () => getDefaultSelectedBodegas(roleId),
    [roleId]
  );
  const bodegaFilterOptions = useMemo(
    () => getBodegaFilterOptions(roleId),
    [roleId]
  );
  const validBodegaFilterKeys = useMemo(
    () => getValidBodegaFilterKeys(roleId),
    [roleId]
  );
  const [allProducts, setAllProducts] = useState<Product[]>([]); // Todos los productos
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]); // Productos filtrados
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]); // Productos mostrados (paginados)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterByType, setFilterByType] = useState(false);
  const [selectedType, setSelectedType] = useState<{ label: string; value: boolean } | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showZeroStock, setShowZeroStock] = useState(false);
  const [originalPageSize, setOriginalPageSize] = useState(10);
  
  // Stock Flexy
  const [productosFlexyMap, setProductosFlexyMap] = useState<
    Map<string, FlexyProductStockRecord>
  >(new Map());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Estados de loading
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingFlexyStock, setIsLoadingFlexyStock] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState<Set<number>>(new Set());
  
  // Estado para mostrar columnas detalladas de stock
  const [showDetailedStock, setShowDetailedStock] = useState(true);
  const [selectedBodegas, setSelectedBodegas] = useState<ProductTableStockFilterKey[]>(
    () => getDefaultSelectedBodegas(undefined)
  );

  useEffect(() => {
    setSelectedBodegas((prev) => sanitizeSelectedBodegasForRole(roleId, prev));
  }, [roleId]);
  
  // Estados para sincronización masiva
  const [isMassSyncModalOpen, setIsMassSyncModalOpen] = useState(false);
  const [ofimaProducts, setOfimaProducts] = useState<any[]>([]);

  /** Confirmación al pasar a inactivo desde el switch */
  const [productPendingDeactivate, setProductPendingDeactivate] = useState<Product | null>(null);
  const [estadoSavingIds, setEstadoSavingIds] = useState<Set<number>>(() => new Set());
  
  const navigate = useNavigate();

  // Función para cargar todos los productos
  // Ahora incluye el parámetro search para búsqueda en el servidor
  const fetchAllProducts = useCallback(async () => {
    try {
      setIsLoadingProducts(true);
      const token = localStorage.getItem('accessToken');
      
      // Construir parámetros de consulta
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '1000');
      
      // Agregar parámetro de búsqueda si existe
      if (debouncedSearchTerm.trim()) {
        params.append('search', debouncedSearchTerm.trim());
      }
      
      // Hacemos la consulta con parámetros de búsqueda
      const response = await axiosInstance.get(`/products?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data || !response.data.data) {
        console.error('La respuesta de la API no tiene el formato esperado:', response.data);
        toast.error((t) => (
          <div className="flex items-center justify-between w-full">
            <span>Error al cargar los productos</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
            >
              ×
            </button>
          </div>
        ));
        return;
      }

      const { data } = response.data;
      
      // Formateamos los productos con la información básica
      const formattedProducts = data.map((product: Product) => ({
        ...product,
        price: typeof product.price === 'string' ? parseFloat(product.price) : product.price,
        is_combo: Boolean(product.is_combo),
        estado: product.estado !== undefined && product.estado !== null ? Number(product.estado) : undefined
      }));

      setAllProducts(formattedProducts);
      console.log('📦 Productos cargados:', formattedProducts.length);
      console.log('🔍 Término de búsqueda:', debouncedSearchTerm);
      console.log('📊 URL de consulta:', `/products?${params.toString()}`);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error((t) => (
        <div className="flex items-center justify-between w-full">
          <span>Error al cargar los productos</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
          >
            ×
          </button>
        </div>
      ));
    } finally {
      setIsLoadingProducts(false);
    }
  }, [debouncedSearchTerm]); // Agregar searchTerm como dependencia

  const resolveDisplayStockForProduct = useCallback(
    (product: Product) => {
      const source = allProducts.find((p) => p.product_id === product.product_id) ?? product
      const flexyProduct =
        productosFlexyMap.get(source.sku) ??
        productosFlexyMap.get(normalizeProductSku(source.sku))
      return getStockDisplayValueForBodegas(
        flexyProduct,
        source as unknown as Record<string, unknown>,
        selectedBodegas,
        validBodegaFilterKeys
      )
    },
    [allProducts, productosFlexyMap, selectedBodegas, validBodegaFilterKeys]
  )

  // Función para aplicar filtros localmente
  const applyFilters = useCallback(() => {
    setIsLoadingFilters(true);
    
    let filtered = [...allProducts];
    if (filterByType && selectedType !== null) {
      filtered = filtered.filter(product => product.is_combo === selectedType.value);
    }
    
    // Aplicar filtro de stock
    if (!showZeroStock) {
      filtered = filtered.filter((product) => resolveDisplayStockForProduct(product) > 0);
    }
    
    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.name.localeCompare(b.name);
      }
      return b.name.localeCompare(a.name);
    });
    
    // Mantener tamaño de página original
    const startIndex = (currentPage - 1) * originalPageSize;
    const endIndex = startIndex + originalPageSize;
    
    // Si faltan productos, buscar más para completar la página
    if (filtered.length < endIndex) {
      const needed = endIndex - filtered.length;
      // Buscar productos adicionales que cumplan los filtros
      const additionalProducts = allProducts
        .filter(product => !filtered.includes(product))
        .filter(product => {
          // La búsqueda ya se aplicó en el servidor
          
          // Aplicar filtro por tipo
          if (filterByType && selectedType !== null) {
            if (product.is_combo !== selectedType.value) return false;
          }
          
          // Aplicar filtro de stock
          if (!showZeroStock && resolveDisplayStockForProduct(product) <= 0) {
            return false;
          }
          
          return true;
        })
        .slice(0, needed);
      
      filtered = [...filtered, ...additionalProducts];
    }
    
    // Aplicar saldos Flexy
    const productsWithStock = filtered.map((product) => ({
      ...product,
      stock: resolveDisplayStockForProduct(product)
    }));
    
    setFilteredProducts(productsWithStock);
    console.log('✅ Productos filtrados:', productsWithStock.length);
    setIsLoadingFilters(false);
  }, [
    allProducts,
    filterByType,
    selectedType,
    sortOrder,
    showZeroStock,
    currentPage,
    originalPageSize,
    resolveDisplayStockForProduct
  ]);

  // Función para obtener productos paginados
  const getPaginatedProducts = useCallback(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filteredProducts.slice(startIndex, endIndex);
    setDisplayedProducts(paginated);
    
    // Calcular total de páginas
    const total = Math.ceil(filteredProducts.length / pageSize);
    setTotalPages(total);
    
    console.log('📄 Página actual:', currentPage);
    console.log('📊 Productos mostrados:', paginated.length);
    console.log('📋 Total páginas:', total);
  }, [filteredProducts, currentPage, pageSize]);

  // Efecto para aplicar filtros cuando cambien los criterios
  useEffect(() => {
    if (allProducts.length > 0) {
      applyFilters();
    }
  }, [allProducts.length, filterByType, selectedType, sortOrder, showZeroStock, selectedBodegas, applyFilters]);

  // Efecto para aplicar filtros cuando cambie allProducts (nuevos resultados de búsqueda)
  useEffect(() => {
    if (allProducts.length >= 0) { // Cambiar a >= 0 para incluir arrays vacíos
      applyFilters();
    }
  }, [allProducts, applyFilters]);

  // Efecto para recargar productos cuando cambie el término de búsqueda
  useEffect(() => {
    // Resetear a la primera página cuando cambie la búsqueda
    setCurrentPage(1);
    
    // Recargar productos con el nuevo término de búsqueda
    fetchAllProducts();
  }, [debouncedSearchTerm, fetchAllProducts]);

  // Efecto para debounce del término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms de delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Efecto para paginar cuando cambien los productos filtrados
  useEffect(() => {
    getPaginatedProducts();
  }, [filteredProducts, currentPage, pageSize, getPaginatedProducts]);

  // Loading general
  const isLoading = isLoadingProducts || isLoadingFlexyStock || isLoadingFilters;

  // Efecto para monitorear cambios en displayedProducts
  useEffect(() => {
    console.log('🎯 Productos mostrados en tabla:', displayedProducts.length);
    console.log('🔍 Término de búsqueda actual:', searchTerm);
    console.log('⏳ Estado de carga:', isLoading);
  }, [displayedProducts, searchTerm, isLoading]);

  // Efecto para actualizar productos cuando cambie el mapa Flexy
  useEffect(() => {
    if (allProducts.length > 0 && productosFlexyMap.size > 0) {
      applyFilters(); // Re-aplicar filtros para actualizar stock
    }
  }, [allProducts.length, productosFlexyMap, applyFilters]);

  // Tamaño de página usado por filtros (sin acoplarse a fetchAllProducts)
  useEffect(() => {
    setOriginalPageSize(pageSize)
  }, [pageSize])

  // Stock Flexy una sola vez al montar (la lista viene de fetchAllProducts vía debouncedSearchTerm)
  useEffect(() => {
    if (!isInitialLoad) return
    void consultarProductosFlexy()
    setIsInitialLoad(false)
  }, [isInitialLoad])

  const consultarProductosFlexy = async () => {
    let loadingToast: string | undefined;
    try {
      setIsLoadingFlexyStock(true);
      
      // Mostrar toast de carga con botón de cierre
      loadingToast = toast((t) => (
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span>Cargando stock Flexy...</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-2 text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>
      ), {
        duration: Infinity
      });
      
      console.log('🚀 ===== INICIO CONSULTA STOCK FLEXY =====');
      
      const response = await axiosInstance.get<FlexyAllResponse>(FLEXY_PRODUCTS_ALL_PATH);
      const flexyProducts = extractFlexyProductsFromResponse(response.data);
      
      if (flexyProducts.length > 0) {
        console.log(`📊 Total productos Flexy recibidos: ${flexyProducts.length}`);
        
        const newProductosFlexyMap = buildFlexyProductMap(flexyProducts);
        setProductosFlexyMap(newProductosFlexyMap);

        toast.dismiss(loadingToast);
        toast.success((t) => (
          <div className="flex items-center justify-between w-full">
            <span>Stock Flexy actualizado correctamente</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
            >
              ×
            </button>
          </div>
        ));
      } else {
        console.warn('⚠️ No se encontraron productos en la respuesta Flexy');
        toast.dismiss(loadingToast);
        toast.error((t) => (
          <div className="flex items-center justify-between w-full">
            <span>No se encontraron productos en la respuesta Flexy</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
            >
              ×
            </button>
          </div>
        ));
      }
    } catch (error) {
      console.error('❌ Error en la consulta Flexy:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error((t) => (
        <div className="flex items-center justify-between w-full">
          <span>Error al cargar stock Flexy</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
          >
            ×
          </button>
        </div>
      ));
    } finally {
      setIsLoadingFlexyStock(false);
      console.log('🏁 ===== FIN CONSULTA STOCK FLEXY =====');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = await new Promise((resolve) => {
      toast((t) => (
        <div className="flex flex-col">
          <p>¿Estás seguro de que deseas eliminar este producto?</p>
          <div className="flex justify-end space-x-2 mt-2">
            <button onClick={() => { toast.dismiss(t.id); resolve(true); }} className="px-3 py-1 bg-red-500 text-white rounded">Eliminar</button>
            <button onClick={() => { toast.dismiss(t.id); resolve(false); }} className="px-3 py-1 bg-gray-300 rounded">Cancelar</button>
          </div>
        </div>
      ),
        { duration: Infinity }
      );
    });

    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(`/products/${id}`);
      
      // Actualizar productos locales
      setAllProducts(prev => prev.filter(product => product.product_id !== id));
      
      toast.success((t) => (
        <div className="flex items-center justify-between w-full">
          <span>Producto eliminado exitosamente ✅</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-4 text-white hover:text-gray-200 cursor-pointer text-xl font-bold"
          >
            ×
          </button>
        </div>
      ));
    } catch (error: any) {
      console.error('Error al eliminar el producto:', error);
      
      // Extraer información del error de la API
      let errorMessage = 'Error al eliminar el producto';
      let errorDetails: React.ReactNode = '';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Si es un error específico de producto en uso
        if (errorData.error === 'PRODUCTO_EN_USO') {
          errorMessage = errorData.message || 'No se puede eliminar el producto porque está en uso';
          
          // Mostrar detalles de los combos que usan este producto
          if (errorData.combos && errorData.combos.length > 0) {
            errorDetails = (
              <div className="mt-2 text-sm">
                <div className="font-semibold mb-1">Producto usado en {errorData.totalCombos} combo(s):</div>
                <div className="max-h-32 overflow-y-auto">
                  {errorData.combos.map((combo: any, index: number) => (
                    <div key={index} className="mb-1 p-2 bg-red-50 border border-red-200 rounded">
                      <div className="font-medium text-red-800">{combo.nombre}</div>
                      <div className="text-red-600 text-xs">SKU: {combo.sku}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        } else {
          // Otros tipos de errores
          errorMessage = errorData.message || errorMessage;
        }
      }
      
      toast.error((t) => (
        <div className="flex flex-col max-w-md">
          <div className="flex items-center justify-between w-full mb-2">
            <span className="font-semibold text-red-800">{errorMessage}</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 text-red-600 hover:text-red-800 cursor-pointer text-xl font-bold"
            >
              ×
            </button>
          </div>
          {errorDetails && (
            <div className="text-red-700">
              {errorDetails}
            </div>
          )}
        </div>
      ), {
        duration: 8000 // Mostrar por más tiempo para que el usuario pueda leer los detalles
      });
    }
  };

  const beginEstadoSave = (id: number) => {
    setEstadoSavingIds((prev) => new Set(prev).add(id))
  }

  const endEstadoSave = (id: number) => {
    setEstadoSavingIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const persistProductEstado = async (row: Product, nuevoEstado: 0 | 1) => {
    beginEstadoSave(row.product_id)
    try {
      const response = await axiosInstance.get(`/products/${row.product_id}?SinSaldo=true`)
      const raw = response.data as Record<string, unknown>
      const mapped = mapApiProductForPut(raw)
      const merged = { ...mapped, estado: nuevoEstado }
      const body = buildPutBodyFromProduct(merged, row.product_id)
      const putResponse = await axiosInstance.put('/products', body)
      setAllProducts((prev) =>
        prev.map((p) =>
          p.product_id === row.product_id ? { ...p, estado: nuevoEstado } : p
        )
      )
      toast.success(
        nuevoEstado === 1 ? 'Producto activado correctamente' : 'Producto desactivado correctamente'
      )
      const warnings = putResponse.data?.warnings
      if (Array.isArray(warnings) && warnings.length > 0) {
        toast(String(warnings.join(' · ')), { icon: '⚠️' })
      }
    } catch (err: unknown) {
      console.error('Error al actualizar estado del producto:', err)
      const payload =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: unknown } }).response?.data
          : undefined
      const fromErrors = getApiProductErrorMessageTable(payload)
      const errorMessage =
        fromErrors ||
        (payload &&
        typeof payload === 'object' &&
        payload !== null &&
        typeof (payload as { message?: unknown }).message === 'string'
          ? String((payload as { message: string }).message)
          : '') ||
        'No se pudo actualizar el estado del producto.'
      toast.error(errorMessage, { icon: '⚠️', duration: 8000 })
    } finally {
      endEstadoSave(row.product_id)
    }
  }

  const requestEstadoChange = (row: Product, nextChecked: boolean) => {
    if (!hasPermission('manage_products')) return
    if (!nextChecked) {
      setProductPendingDeactivate(row)
      return
    }
    void persistProductEstado(row, 1)
  }

  const handleConfirmDeactivate = () => {
    const row = productPendingDeactivate
    if (!row) return
    setProductPendingDeactivate(null)
    void persistProductEstado(row, 0)
  }

  const handleSyncPrice = async (productId: number, productName: string, sku: string) => {
    // Verificar si ya está sincronizando
    if (syncingProducts.has(productId)) {
      return;
    }

    // Agregar a la lista de productos sincronizando
    setSyncingProducts(prev => new Set(prev).add(productId));

    try {
      const response: PriceSyncResponse = await priceSyncService.syncProductPrice(sku);
      
      // Mostrar notificación de éxito
      if (response.actualizados > 0) {
        toast.success((t) => (
          <div className="flex flex-col max-w-md">
            <div className="flex items-center justify-between w-full mb-2">
              <span className="font-semibold text-green-800">✅ Precio sincronizado exitosamente</span>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="ml-4 text-green-600 hover:text-green-800 cursor-pointer text-xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="text-green-700 text-sm">
              <div><strong>Producto:</strong> {productName}</div>
              <div><strong>SKU:</strong> {sku}</div>
              <div><strong>Precio actualizado:</strong> ${response.detalles.exitosos[0]?.precio?.toLocaleString('es-CO') || 'N/A'}</div>
              <div><strong>Origen:</strong> {response.detalles.exitosos[0]?.origen || 'N/A'}</div>
            </div>
          </div>
        ), {
          duration: 6000
        });

        // Actualizar el producto en la lista local si se actualizó exitosamente
        if (response.detalles.exitosos.length > 0) {
          const updatedProduct = response.detalles.exitosos[0];
          setAllProducts(prev => prev.map(product => 
            product.product_id === productId 
              ? { ...product, price: updatedProduct.precio }
              : product
          ));
        }
      } else if (response.sinPrecio > 0) {
        // Mostrar notificación de advertencia para productos sin precio
        toast((t) => (
          <div className="flex flex-col max-w-md">
            <div className="flex items-center justify-between w-full mb-2">
              <span className="font-semibold text-yellow-800">⚠️ Sin precio válido</span>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="ml-4 text-yellow-600 hover:text-yellow-800 cursor-pointer text-xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="text-yellow-700 text-sm">
              <div><strong>Producto:</strong> {productName}</div>
              <div><strong>SKU:</strong> {sku}</div>
              <div><strong>Razón:</strong> {response.detalles.sinPrecio[0]?.razon || 'Precio no disponible'}</div>
            </div>
          </div>
        ), {
          duration: 5000
        });
      } else if (response.errores > 0) {
        // Mostrar notificación de error
        toast.error((t) => (
          <div className="flex flex-col max-w-md">
            <div className="flex items-center justify-between w-full mb-2">
              <span className="font-semibold text-red-800">❌ Error al sincronizar precio</span>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="ml-4 text-red-600 hover:text-red-800 cursor-pointer text-xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="text-red-700 text-sm">
              <div><strong>Producto:</strong> {productName}</div>
              <div><strong>SKU:</strong> {sku}</div>
              <div><strong>Error:</strong> {response.detalles.errores[0]?.error || 'Error desconocido'}</div>
            </div>
          </div>
        ), {
          duration: 6000
        });
      }
    } catch (error: any) {
      console.error('Error sincronizando precio:', error);
      
      let errorMessage = 'Error al sincronizar el precio';
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error((t) => (
        <div className="flex flex-col max-w-md">
          <div className="flex items-center justify-between w-full mb-2">
            <span className="font-semibold text-red-800">❌ Error de sincronización</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 text-red-600 hover:text-red-800 cursor-pointer text-xl font-bold"
            >
              ×
            </button>
          </div>
          <div className="text-red-700 text-sm">
            <div><strong>Producto:</strong> {productName}</div>
            <div><strong>SKU:</strong> {sku}</div>
            <div><strong>Error:</strong> {errorMessage}</div>
          </div>
        </div>
      ), {
        duration: 6000
      });
    } finally {
      // Remover de la lista de productos sincronizando
      setSyncingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };


  const handleFilterByType = (checked: boolean) => {
    setFilterByType(checked);
    if (!checked) {
      setSelectedType(null);
    }
  };

  const handleStockFilterToggle = (isChecked: boolean) => {
    setShowZeroStock(isChecked);
    setCurrentPage(1); // Resetear a la primera página cuando cambie el filtro
  };

  const bodegasSelectionIsDefault = isDefaultBodegasSelection(roleId, selectedBodegas)

  const selectedBodegaFilterOptions = bodegaFilterOptions.filter((opt) =>
    selectedBodegas.includes(opt.value as ProductTableStockFilterKey)
  )

  const handleBodegasFilterChange = (
    newValue: ReadonlyArray<{ label: string; value: string }> | null
  ) => {
    const allowed = validBodegaFilterKeys
    const fields = (newValue ?? [])
      .map((item) => item.value)
      .filter((value): value is ProductTableStockFilterKey =>
        allowed.has(value as ProductTableStockFilterKey)
      )
    setSelectedBodegas(
      fields.length > 0 ? fields : getDefaultSelectedBodegas(roleId)
    )
    setCurrentPage(1)
  }

  // Calcular cantidad de filtros activos (solo cuenta cuando hay valores seleccionados)
  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (searchTerm) count++;
    if (filterByType && selectedType !== null) count++;
    if (!showZeroStock) count++;
    if (!bodegasSelectionIsDefault) count++;
    return count;
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterByType(false);
    setSelectedType(null);
    setShowZeroStock(false);
    setSelectedBodegas([...defaultSelectedBodegas]);
    setCurrentPage(1);
  };

  // Preparar secciones de filtros para FilterPanel
  const filterSections: FilterSection[] = [
    {
      id: 'quick-filters',
      title: 'Filtros Rápidos',
      filters: [
        {
          id: 'type',
          label: 'Filtrar por tipo',
          type: 'switch',
          checked: filterByType,
          onToggle: handleFilterByType,
          selectProps: filterByType ? {
            isMulti: false,
            options: [
              { label: 'Simple', value: 'false' },
              { label: 'Combo', value: 'true' }
            ],
            value: selectedType ? { 
              label: selectedType.label, 
              value: selectedType.value.toString() 
            } : null,
            onChange: (newValue) => {
              if (newValue) {
                setSelectedType({ 
                  label: newValue.label, 
                  value: newValue.value === 'true' 
                });
              } else {
                setSelectedType(null);
              }
            },
            placeholder: 'Seleccionar tipo...'
          } : undefined
        },
        {
          id: 'stock',
          label: 'Mostrar productos sin stock',
          type: 'switch',
          checked: showZeroStock,
          onToggle: handleStockFilterToggle
        }
      ]
    },
    {
      id: 'bodegas',
      title: 'Bodegas',
      filters: [
        {
          id: 'bodegas-select',
          label: 'Bodegas visibles y en stock',
          type: 'select',
          selectProps: {
            isMulti: true,
            options: bodegaFilterOptions,
            value: selectedBodegaFilterOptions,
            onChange: handleBodegasFilterChange,
            placeholder: 'Seleccionar bodegas...'
          }
        }
      ]
    }
  ];

  // Función para manejar la sincronización masiva
  const handleMassSync = async () => {
    try {
      // Obtener productos de Ofima antes de abrir el modal
      const response = await axiosInstance.get('/products/ofima/all');
      setOfimaProducts(response.data.productos || []);
      setIsMassSyncModalOpen(true);
    } catch (error) {
      console.error('Error obteniendo productos de Ofima:', error);
      toast.error('Error al obtener productos de Ofima');
    }
  };

  // Función para manejar el cierre del modal de sincronización masiva
  const handleMassSyncClose = () => {
    setIsMassSyncModalOpen(false);
  };

  // Función para manejar la finalización de la sincronización masiva
  const handleMassSyncComplete = (result: SyncResult) => {
    // Recargar la lista de productos para mostrar los precios actualizados
    if (result.success && result.successCount > 0) {
      setTimeout(() => {
        fetchAllProducts();
      }, 1000);
    }
  };

  const removeFilter = () => {
    setFilterByType(false);
    setSelectedType(null);
  };

  const renderActiveFilters = () => {
    const activeFilters = [];

    if (searchTerm) {
      activeFilters.push(
        <div key="search" className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-50 border border-gray-200 text-gray-700">
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent border-none focus:outline-none w-32 placeholder-gray-400"
          />
          <button onClick={() => setSearchTerm('')} className="ml-2 text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>
      );
    }

    if (filterByType) {
      activeFilters.push(
        <div key="type" className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-50 border border-gray-200 text-gray-700">
          <Select
            options={[
              { label: 'Simple', value: false },
              { label: 'Combo', value: true }
            ]}
            value={selectedType}
            onChange={(newValue) => setSelectedType(newValue as { label: string; value: boolean })}
            placeholder="Tipo..."
            className="react-select-container w-48"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                border: 'none',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                minHeight: 'auto'
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#E5E7EB',
                borderRadius: '0.25rem'
              }),
              placeholder: (base) => ({
                ...base,
                color: '#9CA3AF'
              })
            }}
          />
          <button onClick={removeFilter} className="ml-2 text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>
      );
    }

    if (!showZeroStock) {
      activeFilters.push(
        <div key="stock" className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-red-50 border border-red-200 text-red-700">
          <span>Productos sin stock ocultos</span>
          <button onClick={() => handleStockFilterToggle(true)} className="ml-2 text-red-400 hover:text-red-600">
            ×
          </button>
        </div>
      );
    }

    if (!bodegasSelectionIsDefault) {
      const bodegaLabels =
        selectedBodegaFilterOptions.length > 0
          ? selectedBodegaFilterOptions.map((opt) => opt.label).join(', ')
          : 'Ninguna'
      activeFilters.push(
        <div
          key="bodegas"
          className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-50 border border-indigo-200 text-indigo-800"
        >
          <span>Bodegas: {bodegaLabels}</span>
          <button
            type="button"
            onClick={() => {
              setSelectedBodegas([...defaultSelectedBodegas])
              setCurrentPage(1)
            }}
            className="ml-2 text-indigo-400 hover:text-indigo-600"
            aria-label="Restablecer bodegas"
          >
            ×
          </button>
        </div>
      )
    }

    return activeFilters;
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .toggle-checkbox:checked {
        right: 0;
        border-color: #3b82f6;
      }
      .toggle-checkbox:checked + .toggle-label {
        background-color: #3b82f6;
      }
      .react-select-container {
        width: 100%;
      }
      .react-select__control {
        border-color: #e5e7eb;
        box-shadow: none;
      }
      .react-select__control:hover {
        border-color: #3b82f6;
      }
      .react-select__multi-value {
        background-color: #e5e7eb;
        border-radius: 0.25rem;
      }
      .react-select__multi-value__label {
        color: #374151;
        padding: 0.25rem 0.5rem;
      }
      .react-select__multi-value__remove {
        color: #6b7280;
      }
      .react-select__multi-value__remove:hover {
        background-color: #d1d5db;
        color: #374151;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const columns = [
    {
      header: 'ID',
      accessor: 'product_id' as keyof Product,
      width: '8%',
      key: 'product_id_col'
    },
    {
      header: 'SKU',
      accessor: 'sku' as keyof Product,
      width: '12%',
      key: 'sku_col'
    },
    {
      header: (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:text-blue-500"
          onClick={() => {
            if (sortOrder === 'desc') {
              setSortOrder('asc');
            } else {
              setSortOrder('desc');
            }
            setCurrentPage(1);
          }}
        >
          <span>Nombre</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            {sortOrder === 'asc' ? (
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            ) : (
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            )}
          </svg>
        </div>
      ),
      accessor: 'name' as keyof Product,
      width: '16%',
      maxWidth: '11rem',
      cell: (row: Product) => {
        const { text, full } = displayProductTableName(row.name)
        return (
          <span
            className="block w-full min-w-0 truncate align-top text-left"
            title={full ? full : undefined}
          >
            {text}
          </span>
        )
      },
      key: 'name_col'
    },
    {
      header: 'Estado',
      accessor: 'estado' as keyof Product,
      width: '10%',
      cell: (row: Product) => {
        const active = !isProductInactiveInSearch(row)
        const saving = estadoSavingIds.has(row.product_id)
        if (!hasPermission('manage_products')) {
          return active ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
              Activo
            </span>
          ) : (
            <span
              className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-700"
              title="Producto inactivo"
            >
              Inactivo
            </span>
          )
        }
        return (
          <div
            className="flex items-center gap-2 min-w-[5.5rem]"
            aria-busy={saving}
          >
            <Switch
              checked={active}
              onChange={(next) => requestEstadoChange(row, next)}
              disabled={saving}
              size="sm"
              className="gap-0"
              aria-label={
                saving
                  ? 'Actualizando estado del producto'
                  : active
                    ? 'Desactivar producto (requiere confirmación)'
                    : 'Activar producto'
              }
            />
            {saving ? (
              <ArrowPathIcon
                className="h-4 w-4 shrink-0 animate-spin text-custom-blue"  aria-hidden/>
            ) : (
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {active ? 'Activo' : 'Inactivo'}
              </span>
            )}
          </div>
        )
      },
      key: 'estado_col'
    },
    {
      header: (
        <div className="flex items-center gap-2">
          <span>Stock</span>
          <button
            onClick={() => setShowDetailedStock(!showDetailedStock)}
            className="text-blue-500 hover:text-blue-700 text-lg font-bold"
            title="Ver stock detallado"
          >
            {showDetailedStock ? '−' : '+'}
          </button>
        </div>
      ),
      accessor: 'stock' as keyof Product,
      width: '8%',
      cell: (row: Product) => {
        const displayStock = resolveDisplayStockForProduct(row)
        return (
          <span
            className={`px-2 py-1 rounded-full text-sm ${
              displayStock > 0
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
            title="Suma solo de las bodegas seleccionadas en el filtro">
            {displayStock}
          </span>
        )
      },
      key: 'stock_col'
    },
    // Columnas ocultas (Ecommerce + bodegas según filtro) al expandir stock
    ...(showDetailedStock
      ? bodegaColumns.filter(({ filterKey }) => selectedBodegas.includes(filterKey)).map(
          ({ header, filterKey, key, positiveClass }) => ({
            header,
            accessor: 'stock' as keyof Product,
            width: filterKey === 'fulfillment' ? '10%' : '8%',
            cell: (row: Product) => {
              const flexyProduct = getFlexyProductForSku(productosFlexyMap, row.sku)
              return renderFlexyStockFilterCell(flexyProduct, filterKey, positiveClass)
            },
            key
          })
        )
      : []),
    {
      header: 'Precio',
      accessor: 'price' as keyof Product,
      width: '8%',
      cell: (row: Product) => {
        const flexyProduct = getFlexyProductForSku(productosFlexyMap, row.sku)
        const precio =
          row.price && row.price > 0 ? row.price : resolveFlexyFallbackPrice(flexyProduct)
        return (
          <span className="font-medium">
            ${Number(precio).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
      key: 'base_price_col'
    },
    {
      header: 'Precio+IVA ',
      accessor: 'price' as keyof Product,
      width: '10%',
      cell: (row: Product) => {
        const flexyProduct = getFlexyProductForSku(productosFlexyMap, row.sku)
        const precio =
          row.price && row.price > 0 ? row.price : resolveFlexyFallbackPrice(flexyProduct)
        const priceWithTax = precio * (1 + (row.tax_rate / 100));
        return (
          <span className="font-medium">
            ${Number(priceWithTax).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
      key: 'price_with_tax_col'
    },
    {
      header: 'IVA',
      accessor: 'tax_rate' as keyof Product,
      width: '6%',
      cell: (row: Product) => `${Math.round((row.tax_rate === 0.05 ? 5 : row.tax_rate === 19.00 ? 19 : row.tax_rate))}%`,
      key: 'tax_rate_col'
    },
    {
      header: 'Tipo',
      accessor: 'is_combo' as keyof Product,
      width: '5%',
      cell: (row: Product) => (
        <span
          className={`inline-flex min-w-[1.5rem] justify-center px-2 py-1 rounded-full text-xs font-semibold ${
            row.is_combo
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}
          title={row.is_combo ? 'Combo' : 'Simple'}
        >
          {row.is_combo ? 'C' : 'S'}
        </span>
      ),
      key: 'type_col'
    },
    {
      header: 'Acciones',
      accessor: 'product_id' as keyof Product,
      width: '18%',
      cell: (row: Product) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => navigate(`/products/view/${row.product_id}`)} 
            className="text-green-500 hover:text-green-700" 
            title="Ver"
          >
            <EyeIcon className="h-6 w-6" />
          </button>
          {hasPermission('manage_products') && (
            <button 
              onClick={() => navigate(`/products/edit/${row.product_id}`)} 
              className="text-blue-500 hover:text-blue-700" 
              title="Editar"
            >
              <PencilSquareIcon className="h-6 w-6" />
            </button>
          )}
          {hasPermission('manage_products') && (
            <button 
              onClick={() => handleSyncPrice(row.product_id, row.name, row.sku)} 
              disabled={syncingProducts.has(row.product_id)}
              className={`${
                syncingProducts.has(row.product_id) 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-orange-500 hover:text-orange-700'
              }`}
              title={syncingProducts.has(row.product_id) ? 'Sincronizando...' : 'Sincronizar precio desde Ofima'}
            >
              <ArrowPathIcon className={`h-6 w-6 ${syncingProducts.has(row.product_id) ? 'animate-spin' : ''}`} />
            </button>
          )}
          {hasPermission('delete_products') && (
            <button 
              onClick={() => handleDelete(row.product_id)} 
              className="text-red-500 hover:text-red-700" 
              title="Eliminar"
            >
              <TrashIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      ),
      key: 'actions_col'
    },
  ];

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-full mx-auto">
        {/* Importador de productos */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Productos</h2>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {SHOW_MASS_PRICE_SYNC_BUTTON && hasPermission('manage_products') && (
                <Button
                  onClick={handleMassSync}
                  variant="warning"
                  leftIcon={ArrowPathIcon}
                  className="bg-orange-500 hover:bg-orange-600"
                  title="Sincronizar todos los precios desde Ofima"
                >
                  Sincronizar Precios
                </Button>
              )}
              <ExportProductStockButton roleId={roleId} />
              {/* Botón de filtros con contador */}
              <div className="relative overflow-visible">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="secondary"
                  leftIcon={FunnelIcon}
                  className="relative"
                >
                  Filtros
                  {getActiveFiltersCount() > 0 && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold bg-blue-600 text-white rounded-full min-w-[20px] flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </Button>
                <FilterPanel
                  isOpen={showFilters}
                  onClose={() => setShowFilters(false)}
                  sections={filterSections}
                  onClearAll={handleClearAllFilters}
                  activeFiltersCount={getActiveFiltersCount()}
                  position="right"
                />
              </div>
              {hasPermission('manage_products') && (
                <Button
                  onClick={() => navigate('/products/new')}
                  variant="success"
                  leftIcon={PlusIcon}
                  fullWidth
                  className="sm:w-auto"
                >
                  Crear
                </Button>
              )}
            </div>
          </div>

          {/* Barra de filtros activos */}
          <div className="flex flex-wrap gap-2 mb-4">
            {renderActiveFilters()}
          </div>

          <div className="w-full overflow-hidden mb-20">
            <div className="overflow-x-auto">
              <DataTable
                data={displayedProducts}
                columns={columns}
                className="w-full"
              />
            </div>
            {displayedProducts.length === 0 && !isLoadingProducts && !isLoadingFilters && (
                <div className="text-center py-8">
                  <div className="text-gray-500 text-lg mb-2">
                    {searchTerm ? `No se encontraron productos que coincidan con "${searchTerm}"` : 'No hay productos disponibles'}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Verifica los filtros aplicados'}
                  </div>
                </div>
              )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Confirmación: desactivar desde la tabla */}
      {productPendingDeactivate && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deactivate-product-title"
          onClick={() => setProductPendingDeactivate(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="deactivate-product-title"
              className="text-lg font-semibold text-gray-900 mb-2"
            >
              Desactivar producto
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Al desactivar este producto también se desactivará en Ofima y no podrá usarse
              para crear pedidos en SGL. ¿Desea continuar?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setProductPendingDeactivate(null)}
              >
                Cancelar
              </Button>
              <Button type="button" variant="danger" onClick={handleConfirmDeactivate}>
                Desactivar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sincronización Masiva */}
      <MassSyncModal
        isOpen={isMassSyncModalOpen}
        onClose={handleMassSyncClose}
        onSyncComplete={handleMassSyncComplete}
        ofimaProducts={ofimaProducts}
      />
    </div>
  );
};

export default ProductsTable;